import React, { FunctionComponent, PropsWithChildren, useEffect } from 'react';
import { ArticleModel } from '../../services/content/article/models/ArticleModel';
import { CategoryModel } from '../../services/content/category/models/CategoryModel';
import { getNavbarCategories } from '../../services/configurator/navbar/NavbarConfigurator';
import { getArticlesByAuthorId } from '../../services/content/article/Article';
import { AuthorModel } from '../../services/content/author/models/AuthorModel';
import { getAuthorById } from '../../services/content/author/Author';
import Layout from '../../components/Layout/Layout';
import Head from 'next/head';
import { SectionSecondaryContent } from '../../components/Section/SectionSecondaryContent';
import { NavigationMenuContext } from '../../contexts/NavigationMenuContext';
import styles from '../../styles/AuthorPage.module.scss';
import { setupAuthorsPageTargeting } from '../../services/ads/Targeting';
import { MetaDataModel } from '../../services/content/meta/models/MetaDataModel';
import { Pagination } from '../../components/Partials/Pagination/Pagination';
import { useRouter } from 'next/router';
import { AdBackground } from '../../components/Ads/AdBackground';
import { GoogleAdMobile } from '../../components/Ads/GoogleAdMobile';
import { ads } from '../../services/ads/constants';
import { GoogleAdDesktop } from '../../components/Ads/GoogleAdDesktop';
import { LdJson } from '../../components/LdJson/LdJson';
import { Pages } from '../../constants/Enums';
import { authorCache, categoryCache } from '../../services/Cache';
import S365Picture from '../../components/Image/S365Picture';
import { overrideImageAutoCrop } from '../../services/content/image/ImageConfiguration';
import { ImageRatios, ImageSizes } from '../../services/content/image/models/ImageModel';

type Props = {
    articles: ArticleModel[];
    meta: MetaDataModel;
    categories: CategoryModel[];
    author: AuthorModel | null;
    page: string;
    authorId: string;
};

export async function getServerSideProps(context: any) {
    context.res.setHeader('Cache-Control', 'public, s-maxage=14, stale-while-revalidate=59');

    const authorId = context.query.author[0];
    const page = context.query.author[1] ? context.query.author[1] : '1';

    const cacheKey = `/author?authorId=${authorId}&page=${page}`;

    if (authorCache.get(cacheKey)) {
        const props = categoryCache.get(cacheKey);
        if (props) {
            return {
                articles: props.articles,
                meta: props.meta,
                categories: props.categories,
                author: props.author,
                page: page,
                authorId: authorId,
            };
        }
    }

    const categories = await getNavbarCategories();
    const articleListing = await getArticlesByAuthorId(authorId, '18', page);
    const author = await getAuthorById(authorId);
    // Check if author is null or undefined
    if (author === null) {
        return { notFound: true };
    }
    const articles = articleListing && articleListing.articles ? articleListing.articles : [];
    const meta = articleListing && articleListing.meta ? articleListing.meta : null;

    authorCache.set(cacheKey, {
        articles: articles,
        meta: meta,
        categories: categories,
        author: author,
    });

    return {
        props: {
            articles: articles,
            meta: meta,
            categories: categories,
            author: author,
            page: page,
            authorId: authorId,
        },
    };
}

const customPlaceHolder = '/user-placeholder.png';

const AuthorPage: FunctionComponent<Props> = ({ categories, articles, author, meta, authorId, page }) => {
    const router = useRouter();

    useEffect(() => {
        setupAuthorsPageTargeting(author);
    }, []);

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <title>{author?.name} - woman.bg</title>
                    <LdJson page={Pages.AUTHOR} author={author} />
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window['sprtlTargeting'] = {
                            section: ['author'],
                            author: [${author?.id ?? ''}]
                        }`,
                        }}></script>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('section', 'author') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('author', '${
                             author ? author.id : ''
                         }') : '';
                         `,
                        }}></script>
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/author/${authorId}`} />
                </Head>
                <main className='container'>
                    <div className='row mb-5'>
                        <div className='col-12 col-md-3 mt-2'>
                            <S365Picture
                                className={`${styles.authorImage} normalize-nextjs-image `}
                                url={
                                    author?.avatarUrl
                                        ? overrideImageAutoCrop(author.avatarUrl, ImageRatios.RATIO_4X3, ImageSizes.MID)
                                        : null
                                }
                                alt={author?.name ? `Автор: ${author.name}` : ''}
                                customPlaceHolder={customPlaceHolder}
                            />
                        </div>
                        <div className='col-12 col-md-9'>
                            <h1 className='font-playfair-700'>#{author?.name}</h1>
                            <div className={`${styles.description} font-lato-200`}>{author?.bio}</div>
                        </div>
                    </div>
                    <div className='d-flex flex-row'>
                        <div>
                            <div className='row'>
                                {articles.map((article, idx) => {
                                    return (
                                        <AdsMobileWrapper
                                            idx={idx}
                                            key={`section-secondary-with-mobile-ad-key-${article.id}`}>
                                            <SectionSecondaryContent
                                                className={`${styles.secondaryContentContainer} col-md-4 col-lg-4 col-12 mb-md-3`}
                                                content={article}
                                                category={article?.category}
                                            />
                                        </AdsMobileWrapper>
                                    );
                                })}
                            </div>
                            <div className='row'>
                                <div className='col-12 d-flex flex-row justify-content-end'>
                                    <Pagination
                                        meta={meta}
                                        onPageClick={(page) => {
                                            router.push(
                                                page > 1 ? `/author/${authorId}/${page}` : `/author/${authorId}`,
                                            );
                                        }}
                                        page={page}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='d-none d-md-block col-12 col-md-3 text-center'>
                            <AdBackground className='sticky-ad'>
                                <GoogleAdDesktop ad={ads[10]} />
                            </AdBackground>
                        </div>
                    </div>
                </main>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default AuthorPage;

const AdsMobileWrapper: FunctionComponent<PropsWithChildren<{ idx: number }>> = ({ children, idx }) => {
    const SECOND_ARTICLE = 1;
    const FOURTH_ARTICLE = 3;
    const TENTH_ARTICLE = 9;
    const FIFTEENTH_ARTICLE = 14;

    if (SECOND_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[10]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (FOURTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center ad-container-h600'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[11]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (TENTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[12]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    if (FIFTEENTH_ARTICLE === idx) {
        return (
            <>
                {children}
                <div className='d-inline-block d-md-none d-lg-none text-center'>
                    <AdBackground className='sticky-ad'>
                        <GoogleAdMobile ad={ads[13]} />
                    </AdBackground>
                </div>
            </>
        );
    }

    return <>{children}</>;
};
